/**
 * useReservedFieldSlugToast — wspólny komunikat „pole ma nazwę techniczną zarezerwowaną przez system”.
 *
 * Serwer odpowiada `{ error: 'FIELD_SLUG_RESERVED', slug }`, gdy trasa buduje tabelę kartoteki
 * własnej z istniejących pól (duplikacja, przywrócenie z kosza, instalacja i aktualizacja
 * szablonu), a któreś pole ma nazwę techniczną zarezerwowaną dopiero po jego utworzeniu (np.
 * „decisions”). Hak zwraca funkcję, którą wołający wpina na początku obsługi błędu mutacji:
 *
 *   if (showReservedSlugToast(err, { title: …, variant: 'support' })) return;
 *
 * Gdy to ten błąd, pokazuje JEDEN komunikat (tytuł = ogólny błąd akcji wołającego, opis = nazwa
 * pola i co dalej) i zwraca `true`; inaczej nic nie pokazuje i zwraca `false` — wołający obsługuje
 * wtedy swoje pozostałe błędy po staremu.
 *
 * Komunikat zostaje do ręcznego zamknięcia (`duration: 0`): to treść do przekazania opiekunowi lub
 * do poprawienia w szablonie, nie chwilowe powiadomienie. Żeby kolejne kliknięcie nie układało
 * stosu takich komunikatów, hak zamyka swój poprzedni komunikat, zanim pokaże nowy.
 *
 * Warianty opisu: `support` — użytkownik nie zmieni nazwy sam (klient: kartoteka, kosz,
 * aktualizacja) → „skontaktuj się z opiekunem”; `template` — czyta to autor szablonu w panelu ITE
 * → „zmień nazwę techniczną pola w szablonie i opublikuj nową wersję”.
 *
 * Ten sam kod zwraca też DODANIE pola (kreator modułu, „Dodaj nowe pole” w Ustawieniach) — tam opis
 * daje zwykła funkcja `reservedFieldSlugRenameDescription` niżej, w zwykłym (znikającym)
 * komunikacie okna, bo użytkownik poprawia nazwę od razu.
 */

import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { useToast } from '@/contexts/ToastContext';
import { extractReservedFieldSlug } from '@/lib/errors';

/**
 * Opis odmowy przy DODAWANIU pola (kreator modułu, „Dodaj nowe pole” w Ustawieniach), gdy nazwa
 * techniczna wyliczona z nazwy pola jest zarezerwowana przez system — „nadaj polu inną nazwę”;
 * `null` = to nie ten błąd. Świadomie bez haka niżej: tu użytkownik poprawia nazwę od razu w tym
 * samym oknie, więc wystarcza zwykły komunikat błędu okna (znika sam), a nie komunikat do ręcznego
 * zamknięcia, który hak pokazuje przy kopii / przywróceniu / szablonie (tam treść trzeba przekazać
 * dalej).
 */
export function reservedFieldSlugRenameDescription(err: unknown, t: TFunction): string | null {
  const slug = extractReservedFieldSlug(err);
  return slug === null ? null : t('common.reservedFieldSlug.rename', { slug });
}

/** Komu komunikat każe działać: opiekunowi (`support`) czy autorowi szablonu (`template`). */
export type ReservedFieldSlugVariant = 'support' | 'template';

export interface ReservedFieldSlugToastOptions {
  /** Tytuł komunikatu — dotychczasowy ogólny błąd akcji wołającego. */
  title: string;
  variant: ReservedFieldSlugVariant;
}

/** `true` = to był błąd zarezerwowanej nazwy pola i komunikat został pokazany. */
export type ShowReservedFieldSlugToast = (
  err: unknown,
  opts: ReservedFieldSlugToastOptions,
) => boolean;

export function useReservedFieldSlugToast(): ShowReservedFieldSlugToast {
  const { t } = useTranslation();
  const { show, dismiss } = useToast();
  // Id ostatniego komunikatu pokazanego przez TEN hak — do zamknięcia przed pokazaniem kolejnego.
  const lastToastId = useRef<string | null>(null);

  return useCallback(
    (err, opts) => {
      const slug = extractReservedFieldSlug(err);
      if (slug === null) return false;

      if (lastToastId.current !== null) dismiss(lastToastId.current);
      lastToastId.current = show({
        variant: 'error',
        title: opts.title,
        description:
          opts.variant === 'support'
            ? t('common.reservedFieldSlug.support', { slug })
            : t('common.reservedFieldSlug.template', { slug }),
        duration: 0,
      });
      return true;
    },
    [t, show, dismiss],
  );
}
